import { LiveData } from '@toeverything/infra';

import { DeadlineTimer, RetryBackoff } from '../../../utils/deadline-timer';
import { offlineAccessWarningLeadMs } from '../offline-access-policy';
import type { PermissionSnapshotStore } from '../stores/permission-snapshot';

export interface OfflineAccessWarning {
  /** Earliest expiry over the grants that currently allow access. */
  expiresAt: number;
  remainingMs: number;
}

export interface OfflineAccessSchedulerDeps {
  store: Pick<
    PermissionSnapshotStore,
    | 'getOfflineAccessTtlMs'
    | 'getOfflineAccessExpiresAt'
    | 'getPartitionsDueForRenewal'
    | 'getNextPartitionRenewalAt'
  >;
  /** Replaces one partition with fresh signed grants (online only). */
  renewPartition(partitionIndex: number): Promise<unknown>;
  isOnline(): boolean;
  /** Local OS/system notification; must work without a server. */
  notify(warning: OfflineAccessWarning): void;
  readLastNotifiedExpiresAt(): number | null;
  writeLastNotifiedExpiresAt(expiresAt: number): void;
  now?: () => number;
}

/** Countdown refresh while the warning is visible. */
const WARNING_REFRESH_MS = 60_000;
/**
 * Back-off when a renewal attempt leaves grants due (server error, or the
 * server no longer returns a grant for a doc): 1 min, doubling up to 1 h.
 */
const RENEWAL_RETRY_BASE_MS = 60_000;
const RENEWAL_RETRY_MAX_MS = 60 * 60_000;

/**
 * Client-scheduled offline-access lifecycle: renews signed grants while
 * online, raises the expiry warning after 2/3 of the window and sends one
 * system notification per expiry window. Runs fully offline.
 */
export class OfflineAccessScheduler {
  readonly warning$ = new LiveData<OfflineAccessWarning | null>(null);

  private readonly timer = new DeadlineTimer(() => this.evaluate());
  private readonly retry = new RetryBackoff(
    RENEWAL_RETRY_BASE_MS,
    RENEWAL_RETRY_MAX_MS
  );
  private renewing = false;
  private disposed = false;

  constructor(private readonly deps: OfflineAccessSchedulerDeps) {}

  private now(): number {
    return this.deps.now?.() ?? Date.now();
  }

  /** Reconnect: renew immediately, ignoring the retry back-off. */
  renewNow(): void {
    this.retry.reset();
    this.evaluate();
  }

  evaluate(): void {
    if (this.disposed) return;
    this.timer.clear();
    const now = this.now();
    const { store } = this.deps;
    const wakeups: number[] = [];
    const online = this.deps.isOnline();

    const expiresAt = store.getOfflineAccessExpiresAt(now);
    if (expiresAt === null) {
      this.setWarning(null);
    } else {
      const leadMs = offlineAccessWarningLeadMs(store.getOfflineAccessTtlMs());
      const warnAt = expiresAt - leadMs;
      if (now >= warnAt) {
        const warning = { expiresAt, remainingMs: expiresAt - now };
        this.setWarning(warning);
        if (!online) this.notifyOncePerWindow(warning, leadMs);
        wakeups.push(now + WARNING_REFRESH_MS);
      } else {
        this.setWarning(null);
        wakeups.push(warnAt);
      }
      wakeups.push(expiresAt);
    }

    if (online && !this.renewing) {
      if (now < this.retry.nextAttemptAt) {
        wakeups.push(this.retry.nextAttemptAt);
      } else {
        const due = store.getPartitionsDueForRenewal(now);
        if (due.length > 0) {
          this.renew(due);
        } else {
          const nextRenewalAt = store.getNextPartitionRenewalAt();
          if (nextRenewalAt !== null) wakeups.push(nextRenewalAt);
        }
      }
    }

    this.timer.schedule(wakeups, now);
  }

  dispose(): void {
    this.disposed = true;
    this.timer.clear();
  }

  private renew(partitions: number[]): void {
    this.renewing = true;
    void this.renewOneAfterAnother(partitions).finally(() => {
      this.renewing = false;
      if (this.disposed) return;
      const finishedAt = this.now();
      const stillDue = new Set(
        this.deps.store.getPartitionsDueForRenewal(finishedAt)
      );
      if (partitions.some(partition => stillDue.has(partition))) {
        this.retry.failed(finishedAt);
      } else {
        this.retry.reset();
      }
      this.evaluate();
    });
  }

  /** One partition at a time, so a renewal pass never bursts requests. */
  private async renewOneAfterAnother(partitions: number[]): Promise<void> {
    for (const partitionIndex of partitions) {
      if (this.disposed || !this.deps.isOnline()) return;
      await this.deps.renewPartition(partitionIndex).catch(() => undefined);
    }
  }

  private notifyOncePerWindow(
    warning: OfflineAccessWarning,
    leadMs: number
  ): void {
    const last = this.deps.readLastNotifiedExpiresAt();
    // Grants issued in different batches expire minutes apart; one window
    // covers everything expiring within the warning lead of the last one.
    if (last !== null && warning.expiresAt - last < leadMs) return;
    this.deps.writeLastNotifiedExpiresAt(warning.expiresAt);
    this.deps.notify(warning);
  }

  private setWarning(warning: OfflineAccessWarning | null): void {
    const current = this.warning$.value;
    if (
      current === warning ||
      (current &&
        warning &&
        current.expiresAt === warning.expiresAt &&
        current.remainingMs === warning.remainingMs)
    ) {
      return;
    }
    this.warning$.next(warning);
  }
}
